"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 bg-muted/30 rounded-lg border border-border p-8">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
          <Send className="size-6" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Message Sent!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>How can we help you?</FormLabel>
            <FormControl>
              <Textarea placeholder="Please describe your inquiry in detail..." className="min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px]">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending Message...
          </> : <>
            <Send className="mr-2 size-4" />
            Send Message
          </>}
      </Button>
    </Form>;
}
export default function ContactInformationForm() {
  return <section id="contact-information-form" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground">
            We're locals who know Maryland weather. Quick response, honest pricing.
          </p>
          <p className="text-lg text-muted-foreground mt-4">
            Our team provides accessible, expert exterior services throughout Maryland, ensuring quick response and local knowledge.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {}
          <div className="lg:col-span-7 xl:col-span-8">
            <Card className="border-border shadow-sm">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-2xl font-default font-semibold text-foreground mb-6">
                  Let's Talk About Your Project
                </h3>
                <FormProvider formId="main-contact-form" sectionName="Contact Information Form" defaultValues={{
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                message: ""
              }} submitText="Send Message" successMessage="Thank you for reaching out! We have received your message and will contact you shortly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-5 xl:col-span-4 space-y-8">
            <div>
              <h3 className="text-xl font-default font-semibold text-foreground mb-6">
                How to Reach Us
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="mt-1 bg-primary/10 p-2.5 rounded-md text-primary shrink-0">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">Phone</p>
                    <Link to="tel:+12408991278" className="text-muted-foreground hover:text-primary transition-colors">(240) 899-1278</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 bg-primary/10 p-2.5 rounded-md text-primary shrink-0">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">Email</p>
                    <Link to="mailto:joel@eagleexteriorshomes.com<" className="text-muted-foreground hover:text-primary transition-colors">joel@eagleexteriorshomes.com</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 bg-primary/10 p-2.5 rounded-md text-primary shrink-0">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">Office Address</p>
                    <p className="text-muted-foreground leading-relaxed">100 Oakton Road<br />Gaithersburg, MD 20877</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 bg-primary/10 p-2.5 rounded-md text-primary shrink-0">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">Business Hours</p>
                    <ul className="text-muted-foreground space-y-1">
                      <li className="flex justify-between gap-4">
                        <span>Mon - Fri:</span>
                        <span>8:00 AM - 6:00 PM</span>
                      </li>
                      <li className="flex justify-between gap-4">
                        <span>Saturday:</span>
                        <span>9:00 AM - 2:00 PM</span>
                      </li>
                      <li className="flex justify-between gap-4">
                        <span>Sunday:</span>
                        <span>Closed</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-8" />

            <div>
              <h3 className="text-xl font-default font-semibold text-foreground mb-4">
                Our Location
              </h3>
              <div className="rounded-lg overflow-hidden border border-border shadow-sm bg-muted aspect-video relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/office_location_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/office_location_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/office_location_map.png-wvc-sizes" alt="Map showing our office location" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}